import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { getAppSettings, getPublicSettings } from "@/lib/api";
import { adoptApexDomain, hostKind } from "@/lib/hostKind";
import { publicAppSettingsKey, publicHostKey } from "@/lib/queryClient";

/**
 * Syncs the document's browser-tab branding (title + favicon) with
 * the operator-configured club identity from `/api/settings`.
 *
 * Why this exists: `index.html` ships with `<title>tktic</title>`
 * and `<link rel="icon" href="/favicon.svg">` so the dev/preview
 * shell always has *something*. On prod, the operator uploads a
 * favicon at `/admin/settings`, which the backend serves from
 * `/api/app-assets/favicon`, but nothing in the SPA was rewiring
 * the `<link>` tag — the browser tab kept showing the bundled
 * default. Same story for the page title: every route-specific
 * `useDocumentMeta` call patches the title for that route, but
 * the *baseline* title that shows before any route's hook fires
 * (or on routes that don't call `useDocumentMeta`) was the bundled
 * "tktic".
 *
 * This hook fixes both. It's mounted at the root layout
 * (`PublicAppHeader`) so it runs on every page, fetches the
 * shared `["app-settings"]` cache (also consumed by `/teams`,
 * `GameShell`, `TeamLivePage`), and:
 *
 *   - Sets `document.title` to the configured Club title (or
 *     leaves it alone if the title was already overridden by a
 *     route-level `useDocumentMeta`).
 *   - Updates the `<link rel="icon">` href to the uploaded
 *     favicon URL when one is set.
 *
 * Route-level `useDocumentMeta` calls still take precedence for
 * that route (they overwrite `document.title` on mount + restore
 * the prior value on unmount, which becomes the title this hook
 * set most recently — so navigating back to a route that doesn't
 * override the title naturally inherits the Club title again).
 *
 * No-op on no-club hosts: on the apex / admin / super-admin host
 * (`hostKind() !== "club"`) this hook leaves the tab chrome alone so
 * the bundled platform identity (the static `<title>tktic</title>` +
 * `/favicon.svg` from `index.html`) shows, never a club's branding.
 * The backend already forces platform-default branding into the
 * `/api/settings` payload on those hosts (`with_club_branding`); this
 * client gate is defense-in-depth so a future/regressed payload can't
 * re-introduce the bleed.
 */
export function useBrandSync(): void {
  const settingsQuery = useQuery({
    queryKey: publicAppSettingsKey(),
    queryFn: getAppSettings,
    staleTime: 60_000,
  });

  // Apex cross-check (#567): the narrow public-settings payload carries the
  // backend's runtime apex, which we compare against this bundle's baked-in
  // apex to catch a build/environment mismatch. Runs on every page (this hook
  // is mounted at the root) but `staleTime: Infinity` means one fetch per
  // session. Adopts the backend apex on drift (see adoptApexDomain).
  //
  // Host-partitioned like its `app-settings` sibling above: `/api/settings/public`
  // runs through `with_club_branding`, so its branding fields (app_name,
  // club_logo_url, …) are per-club. Per the #565/#586 policy, any per-club
  // anonymous read must use `publicHostKey`, never a bare `unscopedKey(domain)`,
  // so a warm club-A cache can't render on club-B after a subdomain switch.
  // (`club_apex_domain` itself is backend-global, but the query caches the
  // per-club fields too — key it correctly regardless.)
  const publicSettingsQuery = useQuery({
    queryKey: publicHostKey("settings-public"),
    queryFn: getPublicSettings,
    staleTime: Infinity,
  });
  const backendApex = publicSettingsQuery.data?.club_apex_domain;

  useEffect(() => {
    if (!backendApex) return;
    // Adopt the deployment's real apex when the baked one disagrees, then
    // reload ONCE: everything already rendered (nav hrefs, HostGuard's verdict,
    // the route that matched) was derived from the stale value, so re-running
    // the whole document is the only way to leave it consistent. Loop-proof —
    // after the reload `adoptApexDomain` sees agreement and returns false.
    // Same self-heal shape as `useMe`'s stale dev-club-override reload.
    if (adoptApexDomain(backendApex)) window.location.reload();
  }, [backendApex]);

  const clubTitle = (settingsQuery.data?.settings?.club_title ?? "").trim();
  const faviconUrl = settingsQuery.data?.assets?.favicon_url ?? "";

  useEffect(() => {
    // Only a real club host owns the tab branding; apex/admin keep the
    // bundled platform title. Computed in-effect so it re-evaluates with
    // the resolved settings and stays safe under SSR/jsdom (hostKind
    // already guards `typeof window`).
    if (hostKind() !== "club") return;
    if (clubTitle) {
      // Always update — route-level hooks restore to the value
      // that was set the moment they mounted, so when this hook's
      // effect re-runs (e.g. after the operator updates the title
      // in /admin/settings and the cache invalidates), the change
      // propagates to every route that hasn't taken over the title
      // for its own purpose.
      document.title = clubTitle;
    }
  }, [clubTitle]);

  useEffect(() => {
    // Same host gate as the title: leave the bundled /favicon.svg in
    // place on apex/admin so no club's favicon shows on the platform.
    if (hostKind() !== "club") return;
    if (!faviconUrl) return;
    // Prefer the <link rel="icon"> already in index.html. If
    // multiple icon links exist (theme variants), update them all
    // so whichever the browser picks reflects the operator's
    // favicon.
    const links = document.head.querySelectorAll<HTMLLinkElement>(
      'link[rel="icon"], link[rel="shortcut icon"]',
    );
    if (links.length === 0) {
      const link = document.createElement("link");
      link.rel = "icon";
      link.href = faviconUrl;
      document.head.appendChild(link);
      return;
    }
    for (const link of links) {
      link.href = faviconUrl;
      // The bundled fallback declares `type="image/svg+xml"`, but
      // an operator-uploaded favicon could be .ico/.png/.svg. Drop
      // the type hint so the browser sniffs from the response
      // content-type (the backend sets the right mime via
      // `/api/app-assets/favicon`).
      link.removeAttribute("type");
    }
  }, [faviconUrl]);
}
